import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./DealerHome.css";
import analyticsService from "../services/analyticsService";
import { getUserTrackingInfo } from "../utils/userTracking";
import { Loading } from "./components/Loading";
import { ImageWithFallback } from "./components/Utilities";
import suzukiLogo from "./assets/suzuki.svg";
import volkswagenLogo from "./assets/volkswagen.svg";
import hyundaiLogo from "./assets/hyundai.svg";
import hondaLogo from "./assets/honda.svg";
import toyotaLogo from "./assets/toyota.svg";
import tataLogo from "./assets/tata.svg";
import mahindraLogo from "./assets/mahindra.svg";
import skodaLogo from "./assets/skoda.svg";
import kiaLogo from "./assets/kia.svg";
import loc from "./assets/location.svg";
import dropDown from "./assets/dropDownButton.svg";
import search from "./assets/search.svg";
import hatch from "./assets/hatch.svg";
import crossSuv from "./assets/CrossSUV.svg";
import pickTruck from "./assets/pickTrick.svg";
import sedan from "./assets/sedan.svg";
import suv from "./assets/suv.svg";
import van from "./assets/van.svg";
import truck from "./assets/truck.svg";
import downArrow from "./assets/downArrow.svg";
import minusButton from "./assets/minusBtn.svg";
import plusButton from "./assets/plusBtn.svg";
import filter from "./assets/filter.svg";
import CardLayout from "./components/CardLayout";
import apiService from "../services/apiService";
import { useCarContext } from "./CarContext";

// Simple Input Component
const Input = ({ placeholder, className = "", ...props }) => {
  return (
    <input
      type="text"
      placeholder={placeholder}
      className={`search-input ${className}`}
      {...props}
    />
  );
};

// Simple Select Component
const Select = ({ children, placeholder, className = "", value, onChange }) => {
  return (
    <select className={`${className}`} value={value || ""} onChange={onChange}>
      {placeholder && <option value="">{placeholder}</option>}
      {children}
    </select>
  );
};

const SelectOption = ({ value, children }) => {
  return <option value={value}>{children}</option>;
};

// Utility function to format ownership
export const formatOwnership = (num) => {
  if (!num) return "N/A";
  if (num === 1 || "1") return "1st Owner";
  if (num === 2 || "2") return "2nd Owner";
  if (num === 3 || "3") return "3rd Owner";
  return `${num}th Owner`;
};

// Utility function to format time left
const formatTimeLeft = (startDateStr, endDateStr) => {
  if (
    !startDateStr ||
    !endDateStr ||
    typeof startDateStr !== "string" ||
    typeof endDateStr !== "string"
  ) {
    console.warn("Invalid date strings:", { startDateStr, endDateStr });
    return "N/A";
  }
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    console.warn("Invalid date format:", { startDateStr, endDateStr });
    return "N/A";
  }
  const now = new Date();
  if (now < startDate) {
    return "Not Started";
  }
  if (now > endDate) {
    return "Expired";
  }
  const diffMs = endDate - now;
  const diffSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(diffSeconds / (3600 * 24));
  const hours = Math.floor((diffSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  if (days > 0) {
    return `${days}d:${hours.toString().padStart(2, "0")}hr`;
  } else {
    return `${hours}h:${minutes.toString().padStart(2, "0")}m`;
  }
};

export default function DealerHome() {
  const [searchValue, setSearchValue] = useState("");
  const [locationValue, setLocationValue] = useState("");
  const [sortValue, setSortValue] = useState("year-new");
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false); // NEW: For button actions
  const [expandedSections, setExpandedSections] = useState({
    vehicleType: true,
    fuelType: false,
    transmission: false,
  });
  const [selectedFilters, setSelectedFilters] = useState({
    vehicleTypes: [],
    fuelTypes: [],
    transmissionTypes: [],
  });
  const [dealerCities, setDealerCities] = useState([]);
  const [cars, setCars] = useState([]);
  const [myCars, setMyCars] = useState([]);
  const [addedCarIds, setAddedCarIds] = useState(new Set());
  const [removingCarId, setRemovingCarId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { setFilteredCarCount, setCarCount } = useCarContext();

  // Check onboarding status
  useEffect(() => {
    const storedPath = localStorage.getItem("onboardingStatus");
    if (storedPath) {
      navigate(storedPath);
    }
  }, [navigate]);

  // Fetch dealer home data and my cars
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Track page view
        analyticsService.trackPageView("Dealer Home", "/DealerHome");

        const orgId = localStorage.getItem("orgId") || "trustauto";
        const dealerId = localStorage.getItem("uid") || "adminuid";
        const [dealerResponse, myCarsResponse] = await Promise.all([
          apiService.getDealerHomeData(orgId, dealerId),
          apiService.getMyCarsByYard({ orgId, dealerId }),
        ]);
        console.log("Dealer API Response:", dealerResponse.data);
        const sortedDealerCities = [
          ...(dealerResponse.data.dealerCities || []),
        ].sort((a, b) => a.localeCompare(b));
        setDealerCities(sortedDealerCities);
        localStorage.setItem(
          "dealerCities",
          JSON.stringify(sortedDealerCities)
        );
        setCars(dealerResponse.data.cars || []);
        setMyCars(myCarsResponse.cars || []);
        const myCarIds = new Set(
          (myCarsResponse.cars || [])
            .filter((car) => car.state !== "GATE_PASS_REQUESTED")
            .map((car) => car.id)
        );
        setAddedCarIds(new Set(myCarIds));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 401) {
          localStorage.removeItem("authToken");
          navigate("/unauthorized", {
            state: { from: window.location.pathname },
          });
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Set default location if not set
  useEffect(() => {
    if (dealerCities.length > 0 && !locationValue) {
      setLocationValue(dealerCities[0]);
    }
  }, [dealerCities, locationValue]);

  // Handle car removal from My Cars
  useEffect(() => {
    const handleCarRemoved = async () => {
      try {
        const orgId = localStorage.getItem("orgId") || "trustauto";
        const dealerId = localStorage.getItem("uid") || "adminuid";
        const myCarsResponse = await apiService.getMyCarsByYard({
          orgId,
          dealerId,
        });
        const myCarIds = new Set(
          (myCarsResponse.cars || [])
            .filter((car) => car.state !== "GATE_PASS_REQUESTED")
            .map((car) => car.id)
        );
        setAddedCarIds(new Set(myCarIds));
        setMyCars(myCarsResponse.cars || []);
      } catch (error) {
        console.error("Error refreshing My Cars:", error);
        if (error.response?.status === 401) {
          localStorage.removeItem("authToken");
          navigate("/unauthorized", {
            state: { from: window.location.pathname },
          });
        }
      }
    };

    window.addEventListener("storage", handleCarRemoved);
    return () => window.removeEventListener("storage", handleCarRemoved);
  }, [navigate]);

  // Dynamically generate brand logos based on cars data
  const dynamicBrandLogos = useMemo(() => {
    if (!cars) return [];
    const uniqueMakes = [
      ...new Set(
        cars.map((car) => car.make).filter((make) => brandLogosMap[make])
      ),
    ];
    return uniqueMakes.map((make) => ({
      name: make,
      logo: brandLogosMap[make],
    }));
  }, [cars]);

  // Dynamically generate fuel types based on cars data
  const uniqueFuelTypes = useMemo(() => {
    if (!cars) return [];
    const fuels = [...new Set(cars.map((car) => car.fueltype).filter(Boolean))];
    return fuels.map((f) => ({ label: f, value: f }));
  }, [cars]);

  // Dynamically generate transmission types based on cars data
  const uniqueTransmissionTypes = useMemo(() => {
    if (!cars) return [];
    const transmissions = [
      ...new Set(
        cars
          .map((car) => car["Transmission-type"] || car.transmission)
          .filter(Boolean)
      ),
    ];
    return transmissions.map((t) => ({ label: t, value: t }));
  }, [cars]);

  // Filter and sort cars
  const filteredAndSortedCars = useMemo(() => {
    if (!Array.isArray(cars)) return [];

    if (!dealerCities || dealerCities.length === 0) {
      return [];
    }

    const filtered = cars.filter((car) => {
      if (!car) return false;
      if (addedCarIds.has(car.id) || car.state === "GATE_PASS_REQUESTED")
        return false;
      const matchesSearch =
        !searchValue ||
        (car.make || "").toLowerCase().includes(searchValue.toLowerCase()) ||
        (car.model || "").toLowerCase().includes(searchValue.toLowerCase()) ||
        (car.regno || "").toLowerCase().includes(searchValue.toLowerCase());
      const matchesVehicleType =
        selectedFilters.vehicleTypes.length === 0 ||
        selectedFilters.vehicleTypes.includes(car.carType);
      const matchesFuelType =
        selectedFilters.fuelTypes.length === 0 ||
        selectedFilters.fuelTypes.includes(car.fueltype);
      const matchesTransmission =
        selectedFilters.transmissionTypes.length === 0 ||
        selectedFilters.transmissionTypes.includes(
          car["Transmission-type"] || car.transmission
        );
      const matchesBrand = !selectedBrand || car.make === selectedBrand;
      const matchesLocation =
        !locationValue ||
        car.city?.toLowerCase().trim() === locationValue.toLowerCase().trim();
      return (
        matchesSearch &&
        matchesVehicleType &&
        matchesFuelType &&
        matchesTransmission &&
        matchesBrand &&
        matchesLocation
      );
    });

    return filtered.sort((a, b) => {
      if (!a || !b) return 0;
      switch (sortValue) {
        case "year-new":
          return (b.year || 0) - (a.year || 0);
        case "year-old":
          return (a.year || 0) - (b.year || 0);
        case "km-low":
          return (a.kmsrun || 0) - (b.kmsrun || 0);
        case "km-high":
          return (b.kmsrun || 0) - (a.kmsrun || 0);
        case "auction-near":
          return new Date(a["ta-end-date"]) - new Date(b["ta-end-date"]);
        case "auction-far":
          return new Date(b["ta-end-date"]) - new Date(a["ta-end-date"]);
        default:
          return (b.id || "").localeCompare(a.id || "");
      }
    });
  }, [
    cars,
    searchValue,
    selectedFilters,
    sortValue,
    selectedBrand,
    locationValue,
    addedCarIds,
    dealerCities,
  ]);

  // Update filteredCarCount
  useEffect(() => {
    setFilteredCarCount(filteredAndSortedCars.length);
  }, [filteredAndSortedCars.length, setFilteredCarCount]);

  // Handle Add to My Cars with loading
  const handleAddToMyCars = async (car, onComplete) => {
    if (!car || !car.id) return;

    setActionLoading(true); // Show loading
    setRemovingCarId(car.id);

    try {
      const currentUser = {
        uid: localStorage.getItem("uid"),
        name: localStorage.getItem("name"),
        email: localStorage.getItem("email"),
        organization: localStorage.getItem("organization"),
      };
      const userRoles = ["orguserdealer"];
      const userRole = "orguserdealer";
      const userInfo = getUserTrackingInfo(currentUser, userRoles, userRole);

      analyticsService.trackAddToMyCars({
        carMake: car.brand || car.make || "Unknown",
        carModel: car.model || "Unknown",
        carYear: car.year || "Unknown",
        dealerName: userInfo.dealerName,
        supplierName: car.supplierName || car.supplier || "Unknown",
        carId: car.id,
        registrationNumber:
          car.regno ||
          car.registrationNumber ||
          car.regNo ||
          car.registration ||
          "N/A",
        price: car.price || 0,
        location: car.city || car.location || "Unknown",
        userId: userInfo.userId,
        userName: userInfo.userName,
        userRole: userInfo.userRole,
        isDealerRole: userInfo.isDealerRole,
        organizationId: userInfo.organizationId,
      });

      const payload = {
        carId: car.id,
        auctionId: car.auctionId || "",
        city: car.city || "Unknown",
      };
      await apiService.addToMyCars(payload);

      setTimeout(() => {
        setMyCars((prev) => {
          if (prev.some((myCar) => myCar.id === car.id)) return prev;
          setCarCount((count) => count + 1);
          return [...prev, { ...car, yard: car.yard || "Unknown" }];
        });
        setAddedCarIds((prev) => new Set([...prev, car.id]));
        setRemovingCarId(null);
        onComplete();
        setActionLoading(false); // Hide loading
      }, 300);
    } catch (error) {
      console.error("Error adding to My Cars:", error);
      setTimeout(() => {
        setRemovingCarId(null);
        onComplete();
        setActionLoading(false); // Hide loading on error
      }, 300);

      if (error.response?.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/unauthorized", {
          state: { from: window.location.pathname },
        });
      }
    }
  };

  // Handle See Details with loading
  const handleSeeDetails = (car) => {
    setActionLoading(true);
    setTimeout(() => {
      navigate(`/car-details/${car.id}`, { state: { car } });
      setActionLoading(false);
    }, 300);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleVehicleType = (vehicleType) => {
    setSelectedFilters((prev) => ({
      ...prev,
      vehicleTypes: prev.vehicleTypes.includes(vehicleType)
        ? prev.vehicleTypes.filter((type) => type !== vehicleType)
        : [...prev.vehicleTypes, vehicleType],
    }));
  };

  const toggleFuelType = (fuelType) => {
    setSelectedFilters((prev) => ({
      ...prev,
      fuelTypes: prev.fuelTypes.includes(fuelType)
        ? prev.fuelTypes.filter((type) => type !== fuelType)
        : [...prev.fuelTypes, fuelType],
    }));
  };

  const toggleTransmissionType = (transmissionType) => {
    setSelectedFilters((prev) => ({
      ...prev,
      transmissionTypes: prev.transmissionTypes.includes(transmissionType)
        ? prev.transmissionTypes.filter((type) => type !== transmissionType)
        : [...prev.transmissionTypes, transmissionType],
    }));
  };

  const handleBrandClick = (brandName) => {
    setSelectedBrand((prev) => (prev === brandName ? null : brandName));
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <div className="dealer-home">
      {/* Show loading on initial load OR button actions */}
      {(loading || actionLoading) && <Loading />}

      <div className="search-and-brand-section">
        <div className="container">
          <div className="search-and-brand-content">
            <div className="search-content">
              <div className="search-input-wrapper">
                <Input
                  placeholder="Search cars by make, model, or RC number..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="search-input-value"
                />
                <span>
                  <ImageWithFallback
                    src={search}
                    alt="Search"
                    className="search-icon"
                  />
                </span>
                <div className="mobile-filter">
                  <img src={filter} alt="Filter" onClick={toggleModal} />
                </div>
              </div>
              <div className="location-selector">
                <span className="location-icon">
                  <ImageWithFallback src={loc} alt="Location" />
                </span>
                <Select
                  value={locationValue}
                  onChange={(e) => setLocationValue(e.target.value)}
                  className="location-select"
                  placeholder="Select a city"
                >
                  {dealerCities.length === 0 ? (
                    <SelectOption value="">No cities available</SelectOption>
                  ) : (
                    dealerCities.map((city, index) => (
                      <SelectOption key={index} value={city}>
                        {city.charAt(0).toUpperCase() + city.slice(1)}
                      </SelectOption>
                    ))
                  )}
                </Select>
                <span className="dropdown-loc-icon">
                  <ImageWithFallback src={dropDown} alt="Dropdown" />
                </span>
              </div>
            </div>
            <div className="brand-content">
              <span className="text-gray-600">Search by Brands</span>
              <div className="brand-logos">
                {dynamicBrandLogos.length > 0 ? (
                  dynamicBrandLogos.map((brand, index) => (
                    <div
                      key={index}
                      className={`brand-logo ${
                        selectedBrand === brand.name ? "selected" : ""
                      }`}
                      onClick={() => handleBrandClick(brand.name)}
                    >
                      <ImageWithFallback src={brand.logo} alt={brand.name} />
                    </div>
                  ))
                ) : (
                  <span>No brands available</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="content-wrapper">
          <div className={`sidebar ${isModalOpen ? "modal-open" : ""}`}>
            <div className="filter-card">
              <h3 className="sort-name">Sort by</h3>
              <div className="sort-filter">
                <Select
                  value={sortValue}
                  className="sort-select"
                  onChange={(e) => setSortValue(e.target.value)}
                >
                  <SelectOption value="year-new">
                    Year: Newest First
                  </SelectOption>
                  <SelectOption value="year-old">
                    Year: Oldest First
                  </SelectOption>
                  <SelectOption value="km-low">KM: Low to High</SelectOption>
                  <SelectOption value="km-high">KM: High to Low</SelectOption>
                  <SelectOption value="auction-near">
                    Auction: Nearest
                  </SelectOption>
                  <SelectOption value="auction-far">
                    Auction: Farthest
                  </SelectOption>
                </Select>
                <span className="dropdown-icon">
                  <ImageWithFallback src={downArrow} alt="Down Arrow" />
                </span>
              </div>
              <span className="line">
                <hr />
              </span>
              <h3 className="filter-name">Filter by</h3>
              <div
                className={`filter-section ${
                  expandedSections.vehicleType ? "expanded" : ""
                }`}
              >
                <div
                  className="filter-header"
                  onClick={() => toggleSection("vehicleType")}
                >
                  <span>Vehicle Type</span>
                  <span className="filter-arrow">
                    <span className="minus">
                      <ImageWithFallback
                        src={minusButton}
                        alt="Minus"
                        className="collapse-icon"
                      />
                    </span>
                    <ImageWithFallback
                      src={plusButton}
                      alt="Plus"
                      className="expand-icon"
                    />
                  </span>
                </div>
                <div className="filter-content">
                  <div className="vehicle-types">
                    {vehicleTypes.map((type, index) => (
                      <div
                        key={index}
                        className={`vehicle-type ${
                          selectedFilters.vehicleTypes.includes(type.value)
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => toggleVehicleType(type.value)}
                      >
                        <ImageWithFallback
                          src={type.icon}
                          alt={type.label}
                          className="vehicle-type-icon"
                        />
                        <span className="vehicle-type-label">{type.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className={`filter-section ${
                  expandedSections.fuelType ? "expanded" : ""
                }`}
              >
                <div
                  className="filter-header"
                  onClick={() => toggleSection("fuelType")}
                >
                  <span>Fuel Type</span>
                  <span className="filter-arrow">
                    <span className="minus">
                      <ImageWithFallback
                        src={minusButton}
                        alt="Minus"
                        className="collapse-icon"
                      />
                    </span>
                    <ImageWithFallback
                      src={plusButton}
                      alt="Plus"
                      className="expand-icon"
                    />
                  </span>
                </div>
                <div className="filter-content">
                  <div className="fuel-options">
                    {uniqueFuelTypes.map((fuel, index) => (
                      <div key={index} className="filter-option">
                        <input
                          type="checkbox"
                          id={`fuel-${fuel.value}`}
                          checked={selectedFilters.fuelTypes.includes(
                            fuel.value
                          )}
                          onChange={() => toggleFuelType(fuel.value)}
                        />
                        <label htmlFor={`fuel-${fuel.value}`}>
                          {fuel.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className={`filter-section ${
                  expandedSections.transmission ? "expanded" : ""
                }`}
              >
                <div
                  className="filter-header"
                  onClick={() => toggleSection("transmission")}
                >
                  <span>Transmission</span>
                  <span className="filter-arrow">
                    <span className="minus">
                      <ImageWithFallback
                        src={minusButton}
                        alt="Minus"
                        className="collapse-icon"
                      />
                    </span>
                    <ImageWithFallback
                      src={plusButton}
                      alt="Plus"
                      className="expand-icon"
                    />
                  </span>
                </div>
                <div className="filter-content">
                  <div className="transmission-options">
                    {uniqueTransmissionTypes.map((transmission, index) => (
                      <div key={index} className="filter-option">
                        <input
                          type="checkbox"
                          id={`transmission-${transmission.value}`}
                          checked={selectedFilters.transmissionTypes.includes(
                            transmission.value
                          )}
                          onChange={() =>
                            toggleTransmissionType(transmission.value)
                          }
                        />
                        <label htmlFor={`transmission-${transmission.value}`}>
                          {transmission.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="main-content">
            <div className="car-grid-container">
              {loading ? null : filteredAndSortedCars.length === 0 ? (
                <div className="no-results">
                  <h3>No cars found</h3>
                  {!dealerCities || dealerCities.length === 0 ? (
                    <p>
                      You have not been assigned to any cities yet. Please
                      contact your administrator to get city assignments.
                    </p>
                  ) : (
                    <p>
                      Try adjusting your filters or search criteria, or check if
                      cars exist for the selected city: {locationValue}
                    </p>
                  )}
                </div>
              ) : (
                <div className="car-grid">
                  {filteredAndSortedCars.map((car) => (
                    <CardLayout
                      key={car.id}
                      car={{
                        ...car,
                        startDate: car["start-date"],
                        endDate: car["ta-end-date"] || car["ta_end_date"],
                        yard: car.yard || "Unknown",
                        brand: car.make,
                        rto: car.regno,
                        km: car.kmsrun,
                        fuel: car.fueltype,
                        transmission:
                          car["Transmission-type"] ||
                          car.transmission ||
                          "Manual",
                        start_date: car["start-date"],
                        end_date: car["ta-end-date"],
                        location: car.yard,
                        image:
                          car.car_image_urls && car.car_image_urls.length > 0
                            ? car.car_image_urls[0]
                            : car.logoUrl,
                        owner: formatOwnership(car.ownership),
                        auctionId: car.auctionId || "",
                        rc:
                          car.rcstatus?.toUpperCase() === "YES" ||
                          car.rcstatus?.toUpperCase() === "Y" ||
                          car.rcstatus?.toUpperCase() === "AVAILABLE",
                      }}
                      btn1={handleAddToMyCars}
                      btn2={handleSeeDetails}
                      btn1_label="Add to My Cars"
                      btn2_label="See Details"
                      isDisabled={
                        addedCarIds.has(car.id) || removingCarId === car.id || actionLoading
                      }
                      showBidInfo={false}
                      isRemoving={removingCarId === car.id}
                      enableRemoveAnimation={true}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={toggleModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={toggleModal}>
              ×
            </button>
            <div className="filter-card">
              <h3 className="sort-name">Sort by</h3>
              <div className="sort-filter">
                <Select
                  value={sortValue}
                  className="sort-select"
                  onChange={(e) => setSortValue(e.target.value)}
                >
                  <SelectOption value="year-new">
                    Year: Newest First
                  </SelectOption>
                  <SelectOption value="year-old">
                    Year: Oldest First
                  </SelectOption>
                  <SelectOption value="km-low">KM: Low to High</SelectOption>
                  <SelectOption value="km-high">KM: High to Low</SelectOption>
                  <SelectOption value="auction-near">
                    Auction: Nearest
                  </SelectOption>
                  <SelectOption value="auction-far">
                    Auction: Farthest
                  </SelectOption>
                </Select>
                <span className="dropdown-icon">
                  <ImageWithFallback src={downArrow} alt="Down Arrow" />
                </span>
              </div>
              <h3 className="filter-name">Filter by</h3>
              <div
                className={`filter-section ${
                  expandedSections.vehicleType ? "expanded" : ""
                }`}
              >
                <div
                  className="filter-header"
                  onClick={() => toggleSection("vehicleType")}
                >
                  <span>Vehicle Type</span>
                  <span className="filter-arrow">
                    <span className="minus">
                      <ImageWithFallback
                        src={minusButton}
                        alt="Minus"
                        className="collapse-icon"
                      />
                    </span>
                    <ImageWithFallback
                      src={plusButton}
                      alt="Plus"
                      className="expand-icon"
                    />
                  </span>
                </div>
                <div className="filter-content">
                  <div className="vehicle-types">
                    {vehicleTypes.map((type, index) => (
                      <div
                        key={index}
                        className={`vehicle-type ${
                          selectedFilters.vehicleTypes.includes(type.value)
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => toggleVehicleType(type.value)}
                      >
                        <ImageWithFallback
                          src={type.icon}
                          alt={type.label}
                          className="vehicle-type-icon"
                        />
                        <span className="vehicle-type-label">{type.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className={`filter-section ${
                  expandedSections.fuelType ? "expanded" : ""
                }`}
              >
                <div
                  className="filter-header"
                  onClick={() => toggleSection("fuelType")}
                >
                  <span>Fuel Type</span>
                  <span className="filter-arrow">
                    <span className="minus">
                      <ImageWithFallback
                        src={minusButton}
                        alt="Minus"
                        className="collapse-icon"
                      />
                    </span>
                    <ImageWithFallback
                      src={plusButton}
                      alt="Plus"
                      className="expand-icon"
                    />
                  </span>
                </div>
                <div className="filter-content">
                  <div className="fuel-options">
                    {uniqueFuelTypes.map((fuel, index) => (
                      <div key={index} className="filter-option">
                        <input
                          type="checkbox"
                          id={`fuel-${fuel.value}`}
                          checked={selectedFilters.fuelTypes.includes(
                            fuel.value
                          )}
                          onChange={() => toggleFuelType(fuel.value)}
                        />
                        <label htmlFor={`fuel-${fuel.value}`}>
                          {fuel.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className={`filter-section ${
                  expandedSections.transmission ? "expanded" : ""
                }`}
              >
                <div
                  className="filter-header"
                  onClick={() => toggleSection("transmission")}
                >
                  <span>Transmission</span>
                  <span className="filter-arrow">
                    <span className="minus">
                      <ImageWithFallback
                        src={minusButton}
                        alt="Minus"
                        className="collapse-icon"
                      />
                    </span>
                    <ImageWithFallback
                      src={plusButton}
                      alt="Plus"
                      className="expand-icon"
                    />
                  </span>
                </div>
                <div className="filter-content">
                  <div className="transmission-options">
                    {uniqueTransmissionTypes.map((transmission, index) => (
                      <div key={index} className="filter-option">
                        <input
                          type="checkbox"
                          id={`transmission-${transmission.value}`}
                          checked={selectedFilters.transmissionTypes.includes(
                            transmission.value
                          )}
                          onChange={() =>
                            toggleTransmissionType(transmission.value)
                          }
                        />
                        <label htmlFor={`transmission-${transmission.value}`}>
                          {transmission.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const vehicleTypes = [
  { icon: crossSuv, label: "Crossover SUV", value: "Crossover SUV" },
  { icon: hatch, label: "Hatch Back", value: "Hatchback" },
  { icon: pickTruck, label: "Pickup Truck", value: "Pickup Truck" },
  { icon: sedan, label: "Sedan", value: "Sedan" },
  { icon: suv, label: "SUV", value: "SUV" },
  { icon: van, label: "Van", value: "Van" },
  { icon: truck, label: "Truck", value: "Truck" },
];

const brandLogosMap = {
  "Maruti Suzuki": suzukiLogo,
  Volkswagen: volkswagenLogo,
  Hyundai: hyundaiLogo,
  Honda: hondaLogo,
  Toyota: toyotaLogo,
  Tata: tataLogo,
  Mahindra: mahindraLogo,
  Skoda: skodaLogo,
  Kia: kiaLogo,
};